import React, { forwardRef, useCallback, useLayoutEffect, useRef } from "react"
import { filterDomProps } from "./dom-props"
import { flattenStyle } from "./style"

class AnimatedValue {
  constructor(public value: number) {}
  interpolate(config: { inputRange?: number[]; outputRange?: number[] }) {
    const output = config.outputRange?.[0]
    return output ?? this.value
  }
}

type LayoutEvent = {
  nativeEvent: {
    layout: { x: number; y: number; width: number; height: number }
  }
}

type AnimatedDivProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: unknown
  onLayout?: (event: LayoutEvent) => void
}

const AnimatedDiv = forwardRef<HTMLDivElement, AnimatedDivProps>(function AnimatedDiv(
  { style, onLayout, ...props },
  ref,
) {
  const nodeRef = useRef<HTMLDivElement | null>(null)

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node || !onLayout) return

    const fire = () => {
      const rect = node.getBoundingClientRect()
      onLayout({
        nativeEvent: {
          layout: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
          },
        },
      })
    }

    fire()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(fire)
    observer.observe(node)
    return () => observer.disconnect()
  }, [onLayout])

  return (
    <div
      ref={setRef}
      style={flattenStyle(style as never)}
      {...(filterDomProps(props as Record<string, unknown>) as React.HTMLAttributes<HTMLDivElement>)}
    />
  )
})

/** Web stub：仅存储值，不触发 React 重渲染 */
export function useSharedValue<T>(initial: T) {
  const valueRef = React.useRef(initial)
  return React.useRef({
    get value() {
      return valueRef.current
    },
    set value(v: T) {
      valueRef.current = v
    },
  }).current
}

export function useAnimatedStyle(factory: () => Record<string, unknown>) {
  return factory()
}

export function useAnimatedProps(factory: () => Record<string, unknown>) {
  return factory()
}

export function withTiming(value: number, _config?: { duration?: number }, callback?: () => void) {
  if (callback) queueMicrotask(callback)
  return value
}

export function withSpring(value: number, _config?: object, callback?: () => void) {
  if (callback) queueMicrotask(callback)
  return value
}

export function withSequence(...values: number[]) {
  return values[values.length - 1] ?? 0
}

export function withRepeat(value: number) {
  return value
}

export function interpolate(value: number, _input: number[], output: number[]) {
  return output[0] ?? value
}

export const Extrapolate = { CLAMP: "clamp", EXTEND: "extend", IDENTITY: "identity" }

export const Easing = {
  linear: (t: number) => t,
  ease: (t: number) => t,
  bezier: () => (t: number) => t,
  in: (fn: (t: number) => number) => fn,
  out: (fn: (t: number) => number) => fn,
  inOut: (fn: (t: number) => number) => fn,
}

export function runOnJS<T extends (...args: unknown[]) => unknown>(fn: T) {
  return fn
}

export function cancelAnimation(_sharedValue?: unknown) {}

const Animated = {
  View: AnimatedDiv,
  Text: AnimatedDiv,
  Value: AnimatedValue,
  timing: (value: AnimatedValue, config: { toValue: number; duration?: number }) => ({
    start: (cb?: () => void) => {
      value.value = config.toValue
      cb?.()
    },
  }),
  spring: (value: AnimatedValue, config: { toValue: number }) => ({
    start: (cb?: () => void) => {
      value.value = config.toValue
      cb?.()
    },
  }),
  createAnimatedComponent: <P extends object>(Comp: React.ComponentType<P>) => Comp,
}

export default Animated
